/* eslint-disable @typescript-eslint/no-explicit-any */
import { cookie } from '@turnercode/cdp-cookie';
import { CCPA_LOCATIONS } from './utils/constants';
import { USPrivacyString } from './USPrivacyString';

export type USPData = {
  version: number;
  uspString?: string;
  uspapiLoaded?: boolean;
};

export type USPAPICall = {
  __uspapiCall?: {
    command: string;
    parameter: any;
    version: number;
    callId: string;
  };
};

export type USPAPIReturn = {
  __uspapiReturn?: {
    returnValue: string;
    success: boolean;
    callId: string;
  };
};

export interface USPAPI {
  __uspapi(command: string, version: number, callback: (...args: any) => void, parameter?: any): void;

  __uspapi(command: 'getUSPData', version: 1, callback: (uspData: USPData, success: boolean) => void): void;

  __uspapi(command: 'ping', version: 1, callback: (uspData: USPData, success: boolean) => void): void;
}

export type WindowWithUSPAPI = Window & USPAPI;

const win = window as any;

export const uspString = new USPrivacyString();

/**
 * Internal function to find a frame, if it exists
 *
 * @param {string} locator - Locator string to use to find frame
 * @returns {object} - Child frame object if found, else null
 */
export function findFrame(locator: string): HTMLElement | HTMLIFrameElement {
  let cmpFrame = null;
  let w: Window;

  for (w = win; w; w = w.parent) {
    try {
      if (w.frames && w.frames[locator]) {
        cmpFrame = w;
        break;
      }
    } catch (e) {
      throw new Error(e);
    }
    if (w === win.top) {
      break;
    }
  }
  return cmpFrame;
}

function createIabStub(locatorName: string): void {
  const addFrame = (): boolean => {
    if (!win.frames[locatorName]) {
      if (document.body) {
        const iframe = document.createElement('iframe');

        iframe.style.cssText = 'display:none';
        iframe.name = locatorName;
        document.body.appendChild(iframe);
      } else {
        setTimeout(addFrame, 5);
      }
      return true;
    }
    return false;
  };

  const uspMsgHandler = (event: MessageEvent) => {
    try {
      const msgIsString = typeof event.data === 'string';
      let i: USPAPICall['__uspapiCall'];
      let json: USPAPICall;

      try {
        json = msgIsString ? JSON.parse(event.data) : event.data;
      } catch (e) {
        json = {};
      }
      if (typeof json === 'object' && json !== null && '__uspapiCall' in json) {
        i = json.__uspapiCall;
        win.__uspapi(
          i.command,
          i.version,
          function (retValue: string, success: boolean) {
            const returnMsg: USPAPIReturn = {};

            returnMsg.__uspapiReturn = {
              returnValue: retValue,
              success,
              callId: i.callId,
            };

            if (event.source instanceof Window) {
              event.source.postMessage(msgIsString ? JSON.stringify(returnMsg) : returnMsg, '*');
            }
          },
          i.parameter
        );
      }
    } catch (e) {
      // TODO - log error ... errorLogger('Privacy', '_uspMsgHandler', e);
    }
  };

  const usingUspFrame = findFrame('__uspapiLocator');

  /* CCPA IAB handler must always be defined, even in GDPR regions... */
  if (usingUspFrame === null) {
    /* But only for the top frame */
    addFrame();
    win.__uspapi['msgHandler'] = uspMsgHandler;
    win.addEventListener('message', win.__uspapi['msgHandler'], false);
  }
}

export function initPrivacy(enabled = true, countryCode: string) {
  const api = {
    isPrivacyEnabled(): boolean {
      return enabled;
    },
    getUSPString(): string {
      return uspString.getUSPrivacyString();
    },
    getUSPData(): USPData {
      return {
        version: uspString.getVersion(),
        uspString: uspString.getUSPrivacyString(),
      };
    },
    setUSPString(str: string): boolean {
      return uspString.setUSPrivacyString(str);
    },
    ccpaShareData(): string {
      if (CCPA_LOCATIONS.includes(countryCode)) {
        uspString.setUSPrivacyString('1YNN');
      }
      return uspString.getUSPrivacyString();
    },
    ccpaDoNotShare(): string {
      if (CCPA_LOCATIONS.includes(countryCode)) {
        uspString.setUSPrivacyString('1YYN');
      }
      return uspString.getUSPrivacyString();
    },
  };

  win.WM = win.WM || {};
  win.WM.CDP = win.WM.CDP || {};

  if (enabled) {
    win.__uspapi = function __uspapi(
      command: string,
      version: number,
      callback: (uspData: USPData, success: boolean) => void
    ): void {
      if (command === 'getUSPData' && Number(version) === 1) {
        callback({ version, uspString: uspString.getUSPrivacyString() }, true);
      } else if (command === 'ccpaDoNotShare') {
        callback({ version, uspString: api.ccpaDoNotShare() }, true);
      } else if (command === 'ccpaShareData') {
        callback({ version, uspString: api.ccpaShareData() }, true);
      } else if (command === 'ping') {
        callback({ version, uspapiLoaded: true }, true);
      } else {
        callback(null, false);
      }
    };
    createIabStub('__uspapiLocator');

    const str = cookie.get('usprivacy') as string;

    if (str) {
      uspString.setUSPrivacyString(str);
    } else if (CCPA_LOCATIONS.includes(countryCode)) {
      uspString.setUSPrivacyString('1YNN');
    } else {
      uspString.setUSPrivacyString('1---');
    }

    Object.assign(win.WM.CDP, api);
  }

  return api;
}
